import type { CompanyContactDto } from '@api/customers'
import {
  MolsonAutocomplete,
  MolsonBox,
  MolsonButton,
  MolsonDialog,
  MolsonDialogActions,
  MolsonDialogContent,
  MolsonDialogHeader,
  MolsonInputLabel,
  MolsonTextField,
  MolsonTypography
} from '@components/reusable'
import { useEffect, useRef } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { InputLabelWrapper } from '../styled-components/InputLabelWrapper'

type DeleteContactDialogProps = {
  open: boolean
  onClose: () => void
  contactToDelete: CompanyContactDto
  availableContacts: CompanyContactDto[]
  defaultContactId?: number
  customerName: string
  isDefaultContact: boolean
  onDelete: (
    transferToContactId: number,
    newDefaultContactId?: number
  ) => Promise<void>
  isDeletePending: boolean
}

type FormValues = {
  contactInfo: CompanyContactDto | null
  newDefaultContact: CompanyContactDto | null
}

/**
 * Common dialog component for deleting a contact with transfer logic.
 * Supports both customer-profile and quotation-tool contexts.
 *
 * @param open - Dialog open state
 * @param onClose - Close dialog handler
 * @param contactToDelete - Contact being deleted
 * @param availableContacts - List of contacts to transfer data to (filtered, excluding contactToDelete)
 * @param defaultContactId - ID of the current default contact
 * @param customerName - Name of the customer/company
 * @param isDefaultContact - Whether the contact being deleted is the default
 * @param onDelete - Async handler for deletion (receives transferToContactId and optional newDefaultContactId)
 */
export const DeleteContactDialog = ({
  open,
  onClose,
  contactToDelete,
  availableContacts,
  defaultContactId,
  customerName,
  isDefaultContact,
  onDelete,
  isDeletePending
}: DeleteContactDialogProps) => {
  const { t } = useTranslation('', { keyPrefix: 'common' })

  // Capture the initial state when dialog opens and keep it stable
  // This prevents the UI from changing when data gets refetched during deletion
  const initialIsDefaultContactRef = useRef(isDefaultContact)

  const initialIsDefaultContact = initialIsDefaultContactRef.current

  const {
    control,
    formState: { errors },
    getValues,
    trigger,
    reset
  } = useForm<FormValues>({
    defaultValues: {
      contactInfo: undefined,
      newDefaultContact: undefined
    }
  })

  useEffect(() => {
    if (open && availableContacts.length > 0) {
      initialIsDefaultContactRef.current = isDefaultContact

      // Reset form with fresh data when dialog opens
      const defaultContact = isDefaultContact
        ? availableContacts[0]
        : (availableContacts.find(contact => contact.id === defaultContactId) ??
          availableContacts[0])

      reset({
        contactInfo: defaultContact,
        newDefaultContact: availableContacts[0]
      })
    }
  }, [open, isDefaultContact, availableContacts, defaultContactId, reset])

  const getContactLabel = (contact: string | CompanyContactDto): string => {
    if (typeof contact === 'string') {
      return contact
    }
    let label = ''
    if (contact.firstName || contact.lastName) {
      label = `${contact.firstName ?? ''} ${contact.lastName ?? ''}`.trim()
    } else {
      label = 'N/A'
    }

    let finalLabel = label
    if (contact?.id === defaultContactId) {
      finalLabel = `${label} (default)`
    }
    if (contact?.isLeadContact) {
      finalLabel = `${label} (original)`
    }
    return finalLabel
  }

  const handleDeleteContactInfo = async () => {
    const formValues = getValues()

    if (!formValues.contactInfo) {
      return
    }

    await onDelete(
      Number(formValues.contactInfo.id),
      initialIsDefaultContact
        ? Number(formValues.newDefaultContact?.id)
        : undefined
    )

    // Don't reset or close here - let the parent handle it
    // The parent will close the dialog after all operations complete
  }

  const handleClose = () => {
    if (!isDeletePending) {
      reset()
      onClose()
    }
  }

  const isFormValid = () => {
    const values = getValues()
    if (!values.contactInfo) {
      return false
    }
    if (initialIsDefaultContact && !values.newDefaultContact) {
      return false
    }
    return true
  }

  return (
    <MolsonDialog
      open={open}
      fullWidth={true}
      maxWidth="sm"
      disableScrollLock={true}
      onClose={handleClose}
    >
      <MolsonDialogHeader
        title={t('deleteContact')}
        hasCloseIcon={true}
        handleCloseDialog={handleClose}
      />
      <MolsonDialogContent>
        <MolsonBox sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <MolsonTypography>
            {t('transferQuotesToContact')}{' '}
            <b>
              {contactToDelete?.firstName} {contactToDelete?.lastName}
            </b>
          </MolsonTypography>

          <InputLabelWrapper>
            <MolsonInputLabel>{t('contactInformation')}</MolsonInputLabel>
            <Controller
              name="contactInfo"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('fieldMandatory', {
                    field: t('contactInformation')
                  })
                }
              }}
              render={({ field: { onChange, value, name } }) => (
                <MolsonAutocomplete
                  value={value}
                  onChange={(_, newValue) => {
                    onChange(newValue)
                    trigger('contactInfo')
                  }}
                  options={availableContacts}
                  getOptionLabel={getContactLabel}
                  renderInput={params => (
                    <MolsonTextField
                      {...params}
                      name={name}
                      error={!!errors.contactInfo}
                      helperText={errors.contactInfo?.message}
                    />
                  )}
                />
              )}
            />
          </InputLabelWrapper>

          {initialIsDefaultContact ? (
            <>
              <MolsonTypography mt={2}>
                {t('selectDefaultContact')} <b>{customerName}</b>.
              </MolsonTypography>
              <InputLabelWrapper>
                <MolsonInputLabel htmlFor="newDefaultContact" required={true}>
                  {t('defaultContact')}
                </MolsonInputLabel>
                <Controller
                  name="newDefaultContact"
                  control={control}
                  rules={{
                    required: {
                      value: true,
                      message: t('fieldMandatory', {
                        field: t('defaultContact')
                      })
                    }
                  }}
                  render={({ field: { onChange, value, name } }) => (
                    <MolsonAutocomplete
                      value={value}
                      onChange={(_, newValue) => {
                        onChange(newValue)
                        trigger('newDefaultContact')
                      }}
                      options={availableContacts}
                      getOptionLabel={getContactLabel}
                      renderInput={params => (
                        <MolsonTextField
                          {...params}
                          name={name}
                          error={!!errors.newDefaultContact}
                          helperText={errors.newDefaultContact?.message}
                        />
                      )}
                    />
                  )}
                />
              </InputLabelWrapper>
            </>
          ) : null}
        </MolsonBox>
      </MolsonDialogContent>
      <MolsonDialogActions>
        <MolsonButton
          variant="outlined"
          color="secondary"
          onClick={handleClose}
          disabled={isDeletePending}
        >
          {t('cancel')}
        </MolsonButton>
        <MolsonButton
          variant="contained"
          color="error"
          onClick={handleDeleteContactInfo}
          loading={isDeletePending}
          disabled={!isFormValid() || isDeletePending}
        >
          {t('delete')}
        </MolsonButton>
      </MolsonDialogActions>
    </MolsonDialog>
  )
}
